import { FormEvent, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import {
  GatewayAPIError,
  gatewayAPIClient,
  type AuthLoginInput,
  type AuthRegisterInput,
  type ConfirmRegistrationInput,
  type RegistrationConfirmationResponse,
} from "../../app/api/client";
import { ACTIVE_REGISTRATION_DISCLAIMER_VERSION } from "./registrationDisclaimer";

type AuthMode = "login" | "register";
type PasswordInputType = "password" | "text";
type RegistrationStage = "form" | "confirmation";

interface ModeSwitchStateInput {
  nextMode: AuthMode;
  displayName: string;
  password: string;
  confirmPassword: string;
  disclaimerAccepted: boolean;
}

interface ModeSwitchState {
  displayName: string;
  password: string;
  confirmPassword: string;
  errorMessage: null;
  keepSignedIn: boolean;
  disclaimerAccepted: boolean;
}

export interface RegistrationConfirmationState {
  email: string;
  displayName: string;
  displayEmail: string;
  expiresAt: string;
  resendAvailableAt: string;
}

interface RegistrationStateReset {
  confirmationState: null;
  confirmationCode: string;
  resendStatusKey: null;
}

interface RegistrationStartInput {
  identifier: string;
  displayName: string;
  password: string;
  disclaimerAccepted: boolean;
}

interface RegistrationStartDependencies {
  register(input: AuthRegisterInput): Promise<RegistrationConfirmationResponse>;
}

type RegistrationStartResult =
  | {
      type: "confirmation";
      state: RegistrationConfirmationState;
    }
  | {
      type: "error";
      errorKey: string;
    };

interface RegistrationConfirmDependencies {
  confirmRegistration(input: ConfirmRegistrationInput): Promise<unknown>;
  navigateToApp(): void;
}

type RegistrationConfirmResult =
  | {
      type: "confirmed";
    }
  | {
      type: "error";
      errorKey: string;
    };

interface LoginPageProps {
  initialMode?: AuthMode;
}

const registerDisplayNameMaxLength = 64;

export function normalizeRegisterDisplayName(displayName: string): string {
  return displayName.trim().replace(/\s+/g, " ");
}

export function getRegisterDisplayNameError(displayName: string): string | null {
  const normalized = normalizeRegisterDisplayName(displayName);
  if (normalized === "") {
    return "login.errors.displayNameRequired";
  }
  if (Array.from(normalized).length > registerDisplayNameMaxLength) {
    return "login.errors.displayNameInvalid";
  }
  return null;
}

export function deriveRegisterPayload(
  identifier: string,
  displayName: string,
  password: string,
  disclaimerAccepted: boolean,
): AuthRegisterInput {
  const trimmed = identifier.trim();
  return {
    email: trimmed,
    password,
    display_name: normalizeRegisterDisplayName(displayName),
    registration_disclaimer_accepted: disclaimerAccepted,
    registration_disclaimer_version: ACTIVE_REGISTRATION_DISCLAIMER_VERSION,
  };
}

export function getRegisterIdentifierError(identifier: string): string | null {
  const trimmed = identifier.trim();
  if (trimmed === "" || trimmed.includes("@")) {
    return null;
  }
  return "login.errors.phoneRegistrationUnavailable";
}

export function buildRegistrationConfirmationState(
  identifier: string,
  displayName: string,
  response: RegistrationConfirmationResponse,
): RegistrationConfirmationState {
  return {
    email: identifier.trim(),
    displayName: normalizeRegisterDisplayName(displayName),
    displayEmail: response.email,
    expiresAt: response.expires_at,
    resendAvailableAt: response.resend_available_at,
  };
}

export function resetRegistrationConfirmationState(): RegistrationStateReset {
  return {
    confirmationState: null,
    confirmationCode: "",
    resendStatusKey: null,
  };
}

export async function startRegistrationConfirmation(
  input: RegistrationStartInput,
  dependencies: RegistrationStartDependencies,
): Promise<RegistrationStartResult> {
  const identifierError = getRegisterIdentifierError(input.identifier);
  if (identifierError !== null) {
    return {
      type: "error",
      errorKey: identifierError,
    };
  }
  const displayNameError = getRegisterDisplayNameError(input.displayName);
  if (displayNameError !== null) {
    return {
      type: "error",
      errorKey: displayNameError,
    };
  }

  const response = await dependencies.register(
    deriveRegisterPayload(input.identifier, input.displayName, input.password, input.disclaimerAccepted),
  );
  return {
    type: "confirmation",
    state: buildRegistrationConfirmationState(input.identifier, input.displayName, response),
  };
}

export async function submitRegistrationConfirmation(
  state: RegistrationConfirmationState,
  code: string,
  dependencies: RegistrationConfirmDependencies,
): Promise<RegistrationConfirmResult> {
  const normalizedCode = code.trim();
  if (normalizedCode === "") {
    return {
      type: "error",
      errorKey: "login.errors.confirmationCodeRequired",
    };
  }

  await dependencies.confirmRegistration({
    email: state.email,
    code: normalizedCode,
  });
  dependencies.navigateToApp();
  return {
    type: "confirmed",
  };
}

export function mapErrorMessage(error: unknown): string {
  if (error instanceof GatewayAPIError) {
    switch (error.code) {
      case "invalid_request":
        return "login.errors.invalidRequest";
      case "unauthorized":
        return "login.errors.unauthorized";
      case "identifier_taken":
        return "login.errors.identifierTaken";
      case "disclaimer_required":
        return "login.errors.disclaimerRequired";
      case "phone_registration_unavailable":
        return "login.errors.phoneRegistrationUnavailable";
      case "registration_not_found":
        return "login.errors.registrationNotFound";
      case "invalid_confirmation_code":
        return "login.errors.invalidConfirmationCode";
      case "confirmation_expired":
        return "login.errors.confirmationExpired";
      case "confirmation_rate_limited":
        return "login.errors.confirmationRateLimited";
      case "registration_delivery_unavailable":
        return "login.errors.registrationDeliveryUnavailable";
      default:
        return "login.errors.requestFailed";
    }
  }

  return "login.errors.unexpected";
}

export function isConfirmPasswordVisible(mode: AuthMode): boolean {
  return mode === "register";
}

export function isKeepSignedInVisible(mode: AuthMode): boolean {
  return mode === "login";
}

export function getRegisterConfirmError(password: string, confirmPassword: string): string | null {
  if (confirmPassword.trim() === "") {
    return null;
  }
  if (password !== confirmPassword) {
    return "login.errors.passwordMismatch";
  }
  return null;
}

export function getRegisterFormCanSubmit(
  identifier: string,
  displayName: string,
  password: string,
  confirmPassword: string,
  disclaimerAccepted: boolean,
): boolean {
  if (identifier.trim() === "" || password.trim() === "" || confirmPassword.trim() === "") {
    return false;
  }
  return (
    disclaimerAccepted &&
    getRegisterDisplayNameError(displayName) === null &&
    getRegisterConfirmError(password, confirmPassword) === null
  );
}

export function getPasswordInputType(showPassword: boolean): PasswordInputType {
  return showPassword ? "text" : "password";
}

export function getPasswordAutoComplete(mode: AuthMode): "current-password" | "new-password" {
  return mode === "login" ? "current-password" : "new-password";
}

export function getLoginPersistenceMode(keepSignedIn: boolean): AuthLoginInput["persistence"] {
  return keepSignedIn ? "persistent" : "session";
}

export function buildLoginPayload(identifier: string, password: string, keepSignedIn: boolean): AuthLoginInput {
  return {
    identifier: identifier.trim(),
    password: password.trim(),
    persistence: getLoginPersistenceMode(keepSignedIn),
  };
}

export function buildModeSwitchState(input: ModeSwitchStateInput): ModeSwitchState {
  if (input.nextMode === "login") {
    return {
      displayName: "",
      password: "",
      confirmPassword: "",
      errorMessage: null,
      keepSignedIn: true,
      disclaimerAccepted: false,
    };
  }

  return {
    displayName: input.displayName,
    password: input.password,
    confirmPassword: "",
    errorMessage: null,
    keepSignedIn: true,
    disclaimerAccepted: false,
  };
}

export function LoginPage({ initialMode = "login" }: LoginPageProps = {}) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [mode, setMode] = useState<AuthMode>(initialMode);
  const [identifier, setIdentifier] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [keepSignedIn, setKeepSignedIn] = useState(true);
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [registrationConfirmation, setRegistrationConfirmation] = useState<RegistrationConfirmationState | null>(null);
  const [confirmationCode, setConfirmationCode] = useState("");
  const [resendStatusKey, setResendStatusKey] = useState<string | null>(null);

  const registrationStage: RegistrationStage =
    mode === "register" && registrationConfirmation !== null ? "confirmation" : "form";
  const isConfirmationStage = registrationStage === "confirmation";
  const isBusy = submitting || resending;
  const submitLabel =
    mode === "login"
      ? t("login.actions.signIn")
      : isConfirmationStage
        ? t("login.actions.confirmRegistration")
        : t("login.actions.createAccount");
  const identifierLabel = mode === "register" ? t("login.registerIdentifierLabel") : t("login.identifierLabel");
  const identifierPlaceholder =
    mode === "register" ? t("login.registerIdentifierPlaceholder") : t("login.identifierPlaceholder");
  const confirmPasswordVisible = isConfirmPasswordVisible(mode);
  const keepSignedInVisible = isKeepSignedInVisible(mode);
  const registrationDisclaimerBody = useMemo(() => {
    const translatedBody = t("login.registrationDisclaimer.body", { returnObjects: true }) as unknown;
    if (Array.isArray(translatedBody)) {
      return translatedBody.filter((paragraph): paragraph is string => typeof paragraph === "string" && paragraph.trim() !== "");
    }
    if (typeof translatedBody === "string" && translatedBody.trim() !== "") {
      return [translatedBody];
    }
    return [];
  }, [t]);
  const registerConfirmError = useMemo(
    () => (mode === "register" && !isConfirmationStage ? getRegisterConfirmError(password, confirmPassword) : null),
    [mode, isConfirmationStage, password, confirmPassword],
  );
  const canSubmit = useMemo(() => {
    if (isConfirmationStage) {
      return confirmationCode.trim() !== "";
    }
    if (mode === "login") {
      return identifier.trim() !== "" && password.trim() !== "";
    }
    return getRegisterFormCanSubmit(identifier, displayName, password, confirmPassword, disclaimerAccepted);
  }, [mode, isConfirmationStage, identifier, displayName, password, confirmPassword, disclaimerAccepted, confirmationCode]);

  const onModeChange = (nextMode: AuthMode) => {
    if (mode === nextMode) {
      return;
    }

    const nextState = buildModeSwitchState({
      nextMode,
      displayName,
      password,
      confirmPassword,
      disclaimerAccepted,
    });
    setMode(nextMode);
    setDisplayName(nextState.displayName);
    setPassword(nextState.password);
    setConfirmPassword(nextState.confirmPassword);
    setErrorMessage(nextState.errorMessage);
    setKeepSignedIn(nextState.keepSignedIn);
    setDisclaimerAccepted(nextState.disclaimerAccepted);
    const registrationReset = resetRegistrationConfirmationState();
    setRegistrationConfirmation(registrationReset.confirmationState);
    setConfirmationCode(registrationReset.confirmationCode);
    setResendStatusKey(registrationReset.resendStatusKey);
    setShowPassword(false);
  };

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const normalizedIdentifier = identifier.trim();
    const normalizedPassword = password.trim();
    if (isConfirmationStage && registrationConfirmation !== null) {
      if (confirmationCode.trim() === "") {
        setErrorMessage(t("login.errors.confirmationCodeRequired"));
        return;
      }

      setSubmitting(true);
      setErrorMessage(null);
      setResendStatusKey(null);

      try {
        const result = await submitRegistrationConfirmation(registrationConfirmation, confirmationCode, {
          confirmRegistration: (input) => gatewayAPIClient.confirmRegistration(input),
          navigateToApp: () => navigate("/app", { replace: true }),
        });
        if (result.type === "error") {
          setErrorMessage(t(result.errorKey));
        }
      } catch (error) {
        setErrorMessage(t(mapErrorMessage(error)));
      } finally {
        setSubmitting(false);
      }
      return;
    }

    if (mode === "login" && (normalizedIdentifier === "" || normalizedPassword === "")) {
      setErrorMessage(t("login.errors.identifierPasswordRequired"));
      return;
    }
    if (
      mode === "register" &&
      !getRegisterFormCanSubmit(normalizedIdentifier, displayName, normalizedPassword, confirmPassword.trim(), disclaimerAccepted)
    ) {
      const displayNameError = getRegisterDisplayNameError(displayName);
      if (displayNameError !== null) {
        setErrorMessage(t(displayNameError));
      }
      return;
    }

    setSubmitting(true);
    setErrorMessage(null);

    try {
      if (mode === "login") {
        await gatewayAPIClient.login(buildLoginPayload(normalizedIdentifier, normalizedPassword, keepSignedIn));
        navigate("/app", { replace: true });
      } else {
        const result = await startRegistrationConfirmation(
          {
            identifier: normalizedIdentifier,
            displayName,
            password: normalizedPassword,
            disclaimerAccepted,
          },
          {
            register: (input) => gatewayAPIClient.register(input),
          },
        );
        if (result.type === "error") {
          setErrorMessage(t(result.errorKey));
        } else {
          setRegistrationConfirmation(result.state);
          setConfirmationCode("");
          setResendStatusKey(null);
        }
      }
    } catch (error) {
      setErrorMessage(t(mapErrorMessage(error)));
    } finally {
      setSubmitting(false);
    }
  };

  const onResendRegistrationCode = async () => {
    if (registrationConfirmation === null || isBusy) {
      return;
    }

    setResending(true);
    setErrorMessage(null);
    setResendStatusKey(null);

    try {
      const response = await gatewayAPIClient.resendRegistrationCode({
        email: registrationConfirmation.email,
      });
      setRegistrationConfirmation(
        buildRegistrationConfirmationState(
          registrationConfirmation.email,
          registrationConfirmation.displayName,
          response,
        ),
      );
      setResendStatusKey("login.registrationConfirmation.resendSent");
    } catch (error) {
      setErrorMessage(t(mapErrorMessage(error)));
    } finally {
      setResending(false);
    }
  };

  return (
    <main className="auth-page">
      <section className="auth-card">
        <p className="auth-eyebrow">{t("login.eyebrow")}</p>
        <h1 className="auth-title">
          {mode === "login" ? t("login.titles.signIn") : t("login.titles.register")}
        </h1>
        <p className="auth-description">{t("login.description")}</p>

        <div className="auth-mode-row" role="group" aria-label={t("login.modeSelectorAria")}>
          <button
            className={`auth-mode-button${mode === "login" ? " auth-mode-button-active" : ""}`}
            type="button"
            data-testid="auth-mode-login"
            onClick={() => onModeChange("login")}
            disabled={isBusy}
          >
            {t("login.actions.signIn")}
          </button>
          <button
            className={`auth-mode-button${mode === "register" ? " auth-mode-button-active" : ""}`}
            type="button"
            data-testid="auth-mode-register"
            onClick={() => onModeChange("register")}
            disabled={isBusy}
          >
            {t("login.actions.register")}
          </button>
        </div>

        <form className="auth-form" onSubmit={onSubmit}>
          {isConfirmationStage && registrationConfirmation !== null ? (
            <section
              className="auth-confirmation"
              data-testid="register-confirmation-panel"
              aria-labelledby="register-confirmation-title"
            >
              <h2 className="auth-confirmation-title" id="register-confirmation-title">
                {t("login.registrationConfirmation.title")}
              </h2>
              <p className="auth-confirmation-body">
                {t("login.registrationConfirmation.body", { email: registrationConfirmation.displayEmail })}
              </p>
              <dl className="auth-confirmation-meta">
                <div>
                  <dt>{t("login.registrationConfirmation.emailLabel")}</dt>
                  <dd data-testid="register-confirmation-email">{registrationConfirmation.displayEmail}</dd>
                </div>
                <div>
                  <dt>{t("login.registrationConfirmation.expiresLabel")}</dt>
                  <dd data-testid="register-confirmation-expires">{registrationConfirmation.expiresAt}</dd>
                </div>
              </dl>
              <label className="auth-field">
                <span>{t("login.registrationConfirmation.codeLabel")}</span>
                <input
                  data-testid="register-confirmation-code"
                  autoComplete="one-time-code"
                  inputMode="numeric"
                  value={confirmationCode}
                  onChange={(event) => setConfirmationCode(event.target.value)}
                  placeholder={t("login.registrationConfirmation.codePlaceholder")}
                  disabled={isBusy}
                  required
                />
              </label>
              <div className="auth-confirmation-resend-row">
                <button
                  className="auth-resend-button"
                  data-testid="register-confirmation-resend"
                  type="button"
                  onClick={onResendRegistrationCode}
                  disabled={isBusy}
                >
                  {resending ? t("login.registrationConfirmation.resending") : t("login.registrationConfirmation.resend")}
                </button>
                <span className="auth-confirmation-resend-hint">
                  {t("login.registrationConfirmation.resendAvailable", {
                    time: registrationConfirmation.resendAvailableAt,
                  })}
                </span>
              </div>
              {resendStatusKey !== null ? (
                <p className="auth-status" data-testid="register-confirmation-resend-status">
                  {t(resendStatusKey)}
                </p>
              ) : null}
            </section>
          ) : (
            <>
              <label className="auth-field">
                <span>{identifierLabel}</span>
                <input
                  data-testid="login-identifier"
                  autoComplete="username"
                  inputMode="email"
                  value={identifier}
                  onChange={(event) => setIdentifier(event.target.value)}
                  placeholder={identifierPlaceholder}
                  disabled={submitting}
                  required
                />
              </label>

              {mode === "register" ? (
                <label className="auth-field">
                  <span>{t("login.displayNameLabel")}</span>
                  <input
                    data-testid="register-display-name"
                    autoComplete="name"
                    value={displayName}
                    onChange={(event) => setDisplayName(event.target.value)}
                    placeholder={t("login.displayNamePlaceholder")}
                    aria-describedby="register-display-name-hint"
                    disabled={submitting}
                    required
                  />
                  <span className="auth-field-hint" id="register-display-name-hint">
                    {t("login.displayNameHint")}
                  </span>
                </label>
              ) : null}

              <label className="auth-field">
                <span className="auth-field-head">
                  <span>{t("login.passwordLabel")}</span>
                  <button
                    className="auth-toggle"
                    data-testid={mode === "register" ? "register-password-visibility-toggle" : "login-password-visibility-toggle"}
                    type="button"
                    onClick={() => setShowPassword((current) => !current)}
                    disabled={submitting}
                  >
                    {showPassword ? t("login.actions.hidePassword") : t("login.actions.showPassword")}
                  </button>
                </span>
                <input
                  data-testid={mode === "register" ? "register-password" : "login-password"}
                  type={getPasswordInputType(showPassword)}
                  autoComplete={getPasswordAutoComplete(mode)}
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  placeholder={t("login.passwordPlaceholder")}
                  disabled={submitting}
                  required
                />
              </label>

              {confirmPasswordVisible ? (
                <label className="auth-field">
                  <span>{t("login.confirmPasswordLabel")}</span>
                  <input
                    data-testid="register-confirm-password"
                    type={getPasswordInputType(showPassword)}
                    autoComplete="new-password"
                    value={confirmPassword}
                    onChange={(event) => setConfirmPassword(event.target.value)}
                    placeholder={t("login.confirmPasswordPlaceholder")}
                    disabled={submitting}
                    required
                  />
                </label>
              ) : null}

              {keepSignedInVisible ? (
                <label className="auth-remember">
                  <input
                    data-testid="login-keep-signed-in"
                    type="checkbox"
                    checked={keepSignedIn}
                    onChange={(event) => setKeepSignedIn(event.target.checked)}
                    aria-describedby="login-keep-signed-in-helper"
                    disabled={submitting}
                  />
                  <span className="auth-remember-copy">
                    <span className="auth-remember-label">{t("login.keepSignedIn.label")}</span>
                    <span className="auth-remember-helper" id="login-keep-signed-in-helper">
                      {t("login.keepSignedIn.helper")}
                    </span>
                  </span>
                </label>
              ) : null}

              {mode === "register" ? (
                <section
                  className="auth-disclaimer"
                  data-testid="register-disclaimer-panel"
                  aria-labelledby="register-disclaimer-title"
                >
                  <h2 className="auth-disclaimer-title" id="register-disclaimer-title">
                    {t("login.registrationDisclaimer.title")}
                  </h2>
                  {registrationDisclaimerBody.map((paragraph, index) => (
                    <p className="auth-disclaimer-body" key={index}>
                      {paragraph}
                    </p>
                  ))}
                  <p className="auth-disclaimer-version">
                    <span>{t("login.registrationDisclaimer.versionLabel")}</span>
                    <code data-testid="register-disclaimer-version">{ACTIVE_REGISTRATION_DISCLAIMER_VERSION}</code>
                  </p>
                  <label className="auth-disclaimer-accept">
                    <input
                      data-testid="register-disclaimer-accepted"
                      type="checkbox"
                      checked={disclaimerAccepted}
                      onChange={(event) => setDisclaimerAccepted(event.target.checked)}
                      disabled={submitting}
                      required
                    />
                    <span>{t("login.registrationDisclaimer.acceptLabel")}</span>
                  </label>
                </section>
              ) : null}
            </>
          )}

          {registerConfirmError !== null ? (
            <p className="auth-error" data-testid="register-confirm-error">
              {t(registerConfirmError)}
            </p>
          ) : null}

          {errorMessage !== null ? (
            <p className="auth-error" data-testid="login-error">
              {errorMessage}
            </p>
          ) : null}

          <button className="auth-submit" data-testid="login-submit" type="submit" disabled={isBusy || !canSubmit}>
            {submitting ? t("login.actions.pleaseWait") : submitLabel}
          </button>
        </form>
      </section>
    </main>
  );
}
